import type { RegisterRow } from '@myrewards/contracts';
import { SMALL_GROUP_WORDS } from '@myrewards/domain';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useId, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { meQuery } from '../api';
import { overviewQuery, registerQuery } from '../api/insights';
import { sectorSetupListQuery } from '../api/settings';
import { shareWords, whereItIs } from '../insights/labels';
import { TwoColumns } from '../shell';
import {
  ButtonLink,
  Card,
  CardHelp,
  DataTable,
  EmptyArt,
  EmptyState,
  StatusPill,
  cx,
  formatCount,
  formatMoney,
  formatShortDate,
  HelpShownTag,
  plural,
  useHelpAnchor,
  type DataColumn,
} from '../ui';
import { latestChangeWords } from '../acting-for/doneWords';
import { AskDataEntry } from '../ask-data/AskDataEntry';
import { ExecutiveSetupCard } from './ExecutiveSetupCard';
import { finalSummariesQuery } from '../api/homes';
import { fromFinalSummary } from './finalSummaries';
import { AWARD_TYPE_LABEL, AWARD_TYPE_SHORT, type PoolPosition } from './labels';
import { ledSectors, type ExecutiveModel } from './model';
import s from './Home.module.css';

const BUDGETS_HREF = '/manage/executive/budget-pools';

/**
 * X1 · the Executive's part of Home: what's left to set up (FR3), every pool for the year, the
 * latest awards, final summaries, the Sector Leads and recognition coverage. Each card reads its
 * own data, so a refused or slow read only affects that card.
 */
export function ExecutivePanel({
  ex,
  firstRun,
  meId,
  asOf,
}: {
  ex: ExecutiveModel;
  /** The design-review first-run view: activity cards show their first-day states. */
  firstRun: boolean;
  /** Who is reading: a change made for them reads "for you" (APP-148). */
  meId: string;
  /** Home's day, for when each pool last changed. */
  asOf: string;
}): ReactElement {
  return (
    <>
      <ExecutiveSetupCard ex={ex} />
      <PoolsSection ex={ex} meId={meId} asOf={asOf} />
      <AskDataEntry about="Ask about budgets, spending and recognition in plain words, across sectors and fiscal years. Each answer comes with a chart, the table behind it and how it was worked out, and your conversations are kept for you." />
      <TwoColumns
        sideLabel="Final summaries, Sector Leads and recognition coverage"
        main={<RecentAwardsCard fiscalYear={ex.fiscalYear} firstRun={firstRun} />}
        side={
          <>
            <FinalSummariesCard firstRun={firstRun} />
            <SectorLeadsCard pools={ex.pools} />
            <CoverageCard fiscalYear={ex.fiscalYear} firstRun={firstRun} />
          </>
        }
      />
    </>
  );
}

// --- Budget pools ---------------------------------------------------------------------------------

function percent(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.max(0, Math.min(100, (part / whole) * 100));
}

/**
 * APP-148 (the ExecHome and LeadHome boards): the pool's newest ledger change, who made it and for
 * whom, "for you" when it was the reader. Nothing in the ledger this year: no line.
 */
export function LatestChange({
  pool,
  meId,
  asOf,
  className,
}: {
  pool: PoolPosition;
  meId: string;
  asOf: string;
  className: string;
}): ReactElement | null {
  const latest = pool.latest ?? null;
  if (latest === null) return null;
  return <p className={className}>Latest change: {latestChangeWords(latest, meId, asOf)}</p>;
}

function PoolTile({
  pool,
  year,
  solo,
  meId,
  asOf,
}: {
  pool: PoolPosition;
  year: string;
  /** Some pools are set and this one isn't: it gets its own Set budget, as on the board. */
  solo: boolean;
  meId: string;
  asOf: string;
}): ReactElement {
  const name = pool.pool.name;
  const { budget, remaining } = pool;
  if (budget === null || remaining === null) {
    // X1's board: an unset pool among set ones says where it's set and has a Set budget button
    // (a 44 px target, QA-043). With every pool unset, the banner above sets them all.
    return (
      <li className={cx(s.poolTile, s.poolTileNotSet)}>
        <h3 className={s.poolTileName}>{name}</h3>
        <p className={cx('disp', s.poolTileValue, s.muted)}>Not set</p>
        <p className={s.meta}>
          {pool.kind === 'spot'
            ? `${name} pool · ${
                pool.paid.cashCents + pool.inProgress.cashCents > 0
                  ? `${formatMoney(pool.paid.cashCents + pool.inProgress.cashCents)} awarded`
                  : 'cash'
              } ${year}`
            : `${name} pool · cash and options ${year}`}
        </p>
        {solo && (
          <>
            <p className={s.meta}>Set it in Executive › Budget pools</p>
            <span className={s.tileAction}>
              <ButtonLink
                href={BUDGETS_HREF}
                variant="cyan"
                size="sm"
                aria-label={`Set budget for ${name}`}
              >
                Set budget
              </ButtonLink>
            </span>
          </>
        )}
      </li>
    );
  }
  const usedCash = pool.paid.cashCents + pool.inProgress.cashCents;
  const over = remaining.cashCents < 0;
  // PAAs and TAAs are counted against their pool from submission, like every pool (K34).
  const paaTaa = pool.kind === 'paa_taa';
  return (
    <li className={s.poolTile}>
      <h3 className={s.poolTileName}>
        {name}
        {over && <StatusPill tone="problem" size="sm" label="Over budget" />}
      </h3>
      {/* Over budget in words, never a minus sign alone (QA-036): "$2,913" with "over budget". */}
      <p className={cx('disp', s.poolTileValue, over && s.overBudget)}>
        {paaTaa ? formatMoney(usedCash) : formatMoney(Math.abs(remaining.cashCents))}
      </p>
      <p className={s.meta}>
        {paaTaa
          ? `${name} pool · awarded ${year} · of ${formatMoney(budget.cashCents)}`
          : `${name} pool · ${over ? 'over budget' : 'cash left'} ${year} · of ${formatMoney(budget.cashCents)}`}
      </p>
      <div
        className={s.poolBar}
        role="img"
        aria-label={`${formatMoney(usedCash)} awarded of ${formatMoney(budget.cashCents)}`}
      >
        <span
          className={s.barPaid}
          style={{ width: `${String(percent(usedCash, budget.cashCents))}%` }}
        />
      </div>
      {pool.kind === 'spot' ? (
        // APP-264: Spot Awards are paid in cash, so their pool has no options to count.
        <p className={s.poolTileRow}>
          <span>Options</span>
          <span className={s.strong}>Cash only</span>
        </p>
      ) : (
        <p className={s.poolTileRow}>
          <span>{paaTaa ? 'Options granted' : 'Options left'}</span>
          <span className={s.strong}>
            {paaTaa
              ? formatCount(pool.paid.options + pool.inProgress.options)
              : `${formatCount(remaining.options)} of ${formatCount(budget.options)}`}
          </span>
        </p>
      )}
      <LatestChange pool={pool} meId={meId} asOf={asOf} className={s.meta} />
    </li>
  );
}

function PoolsSection({
  ex,
  meId,
  asOf,
}: {
  ex: ExecutiveModel;
  meId: string;
  asOf: string;
}): ReactElement {
  const headingId = useId();
  const year = String(ex.fiscalYear);
  const total = ex.pools.length;
  const anchor = useHelpAnchor('budget-pools');
  return (
    <section className={s.panel} aria-labelledby={headingId} {...anchor.props}>
      <header className={s.panelHead}>
        <h2 id={headingId} className={cx('disp', s.panelTitle)}>
          {`Budget pools · ${year}`}
        </h2>
        <CardHelp
          topic={`Budget pools · ${year}`}
          shows={`Cash and options left in each ${year} budget pool: the Executive pool, the sector pools and the PAA/TAA pool. A grey card means that pool has no budget yet.`}
          canDo="Open Budget pools to see each pool’s budget and adjustments, or set any budget that’s missing."
        />
        <HelpShownTag shown={anchor.shown} />
        <Link href={BUDGETS_HREF} className={s.panelLink}>
          Budgets and adjustments
        </Link>
      </header>
      {total > 0 && ex.poolsSet === 0 && (
        <div className={s.notSetBanner}>
          <EmptyArt name="pool" size="sm" />
          <p className={s.cardText}>
            <strong>{`No ${year} budgets yet.`}</strong>{' '}
            {`Set all ${countText(total)} pools in one place; each one starts counting as soon as it has a budget.`}
          </p>
          <ButtonLink href={BUDGETS_HREF} variant="navy" size="sm">
            Set all budget pools
          </ButtonLink>
        </div>
      )}
      {total === 0 ? (
        <EmptyState
          variant="not-set"
          art="pool"
          size="sm"
          headline={`No pools to show for ${year}`}
          body="Budget pools appear here once they exist. A System Admin sets up the sectors; Executives set each pool’s budget."
        />
      ) : (
        <ul className={s.poolGrid} aria-label={`Budget pools ${year}`}>
          {ex.pools.map((pool) => (
            <PoolTile
              key={pool.pool.id}
              pool={pool}
              year={year}
              solo={ex.poolsSet > 0}
              meId={meId}
              asOf={asOf}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function countText(n: number): string {
  const words = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  return words[n] ?? String(n);
}

// --- Recent awards ----------------------------------------------------------------------------

function amountCell(row: RegisterRow): ReactElement {
  const sub =
    row.options > 0
      ? `${formatMoney(row.cashCents)} cash · ${plural(row.options, 'option', 'options')}`
      : 'cash';
  return (
    <>
      {formatMoney(row.options > 0 ? row.valueCents : row.cashCents)}
      <span className={s.subId}>{sub}</span>
    </>
  );
}

const RECENT_COLUMNS: DataColumn<RegisterRow>[] = [
  {
    id: 'date',
    // Listed by when each was sent (decision 12), so the date shown is that one too: the award
    // date of a PAA sent today can be weeks earlier (X1 polish, decision 19).
    header: 'Sent',
    cell: (r) =>
      r.sentOn === undefined
        ? r.awardDate === null
          ? 'Not sent'
          : formatShortDate(r.awardDate)
        : r.sentOn === null
          ? 'Not sent'
          : formatShortDate(r.sentOn),
    nowrap: true,
  },
  {
    id: 'award',
    header: 'Award',
    cell: (r) =>
      r.canOpenRecord ? (
        <Link href={`/awards/${r.award.id}`} className={s.strongLink}>
          {r.award.displayId}
        </Link>
      ) : (
        r.award.displayId
      ),
    nowrap: true,
    card: 'title',
  },
  { id: 'type', header: 'Type', cell: (r) => AWARD_TYPE_SHORT[r.type] },
  { id: 'people', header: 'People', cell: (r) => formatCount(r.recipients.count), numeric: true },
  { id: 'amount', header: 'Amount', cell: amountCell, numeric: true },
  {
    id: 'pool',
    header: 'Sector · pool',
    cell: (r) => {
      const pools = r.paidFrom.map((p) => `${p.pool.name} pool`).join(', ');
      // An award with no sector (an Executive Discretionary Bonus) names only its pool.
      return r.sector === null ? (
        pools
      ) : (
        <>
          {r.sector.name}
          <span className={s.subId}>{pools}</span>
        </>
      );
    },
  },
  {
    id: 'where',
    header: 'Where it is',
    cell: (r) => {
      // The stage in the pill, the step under it ("With the Stock Admin team" · "waiting to be
      // claimed"), so the column wraps and the table fits its card at 1440 (QA-042).
      const where = whereItIs(r);
      const [stage = where.label, ...step] = where.label.split(' · ');
      return (
        <>
          <StatusPill tone={where.tone} size="sm" label={stage} />
          {step.length > 0 && <span className={s.subId}>{step.join(' · ')}</span>}
        </>
      );
    },
  },
];

function RecentAwardsCard({
  fiscalYear,
  firstRun,
}: {
  fiscalYear: number;
  firstRun: boolean;
}): ReactElement {
  const year = String(fiscalYear);
  const recent = useQuery({
    // K34's "latest 10": by when each was sent, so a PAA sent today with an earlier award date
    // isn't pushed off the list (QA-031).
    ...registerQuery({ period: 'fiscal_year', fiscalYear, sort: 'sent_newest', size: 10 }),
    enabled: !firstRun,
  });
  const rows = firstRun ? [] : (recent.data?.rows ?? []);
  return (
    <Card
      title="Recent awards"
      helpAnchor="recent-awards"
      help={{
        shows:
          'The 10 most recent awards in the company, from any sector, and where each one is now.',
        canDo:
          'Open an award’s record, or see every award for any time frame in Insights › All awards, with filters, totals and export.',
      }}
      aside={
        <Link href="/insights/awards" className={cx(s.strongLink, s.asideLink)}>
          See all awards
        </Link>
      }
    >
      {recent.isError && !firstRun ? (
        <p className={s.cardText}>The latest awards didn’t load. Reload the page to try again.</p>
      ) : (
        <DataTable
          caption={`Recent awards ${year}`}
          columns={RECENT_COLUMNS}
          rows={rows}
          rowKey={(r) => r.award.id}
          minWidth={720}
          // Below 720 px each award is a card, not a table that scrolls sideways (DESIGN-SPEC §9).
          cards
          empty={
            !firstRun && recent.isPending
              ? { title: 'Loading the latest awards…', body: 'One moment.' }
              : {
                  title: `No awards yet in ${year}.`,
                  body: `The 10 most recent show here the moment they’re entered, from any sector. Starting with history? A System Admin can import the ${year} tracker.`,
                }
          }
        />
      )}
    </Card>
  );
}

// --- Final summaries --------------------------------------------------------------------------

function FinalSummariesCard({ firstRun }: { firstRun: boolean }): ReactElement {
  // Final summaries are for Executive Discretionary Bonuses only (PRD §10.2(8), N2): the one
  // summary the Executives get when one closes, newest close first, with each pool's cash
  // before and after as of the close and how it was decided (APP-072).
  const closed = useQuery({ ...finalSummariesQuery(3), enabled: !firstRun });
  const rows = firstRun ? [] : (closed.data?.summaries ?? []).map(fromFinalSummary);
  return (
    <Card
      title="Final summaries"
      titleSize="sm"
      compact
      help={{
        shows:
          'The summary the Executives get when an Executive Discretionary Bonus closes: who was recognized, the amounts, each pool before and after, and how it was decided.',
        canDo:
          'Open a summary’s record, or see every closed Executive Discretionary Bonus in Insights › All awards.',
      }}
      aside={
        <Link
          href="/insights/awards?type=EDB&stage=closed&sort=closed_newest"
          className={cx(s.strongLink, s.asideLink)}
          aria-label="All final summaries"
        >
          All
        </Link>
      }
    >
      {!firstRun && closed.isPending ? (
        <p className={s.meta}>Loading…</p>
      ) : !firstRun && closed.isError ? (
        <p className={s.cardText}>The summaries didn’t load. Reload the page to try again.</p>
      ) : rows.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="letter"
          size="sm"
          headline="No final summaries yet"
          body="When an Executive Discretionary Bonus closes, you get one summary: people, amounts, and each pool before and after."
        />
      ) : (
        <ul className={s.summaryList}>
          {rows.map((r) => (
            <li key={r.id} className={s.summary}>
              <p className={s.summaryHead}>
                {r.canOpenRecord ? (
                  <Link href={`/awards/${r.id}`} className={s.strongLink}>
                    {r.displayId}
                  </Link>
                ) : (
                  <span className={s.strong}>{r.displayId}</span>
                )}
                <StatusPill tone="done" size="sm" label={`Closed ${formatShortDate(r.closedOn)}`} />
              </p>
              <p className={s.meta}>{`${AWARD_TYPE_LABEL.EDB} · requested by ${r.requestedBy}`}</p>
              <p className={s.summaryFigures}>
                <span>
                  <strong>{formatCount(r.people)}</strong> {r.people === 1 ? 'person' : 'people'}
                </span>
                <span>
                  <strong>{formatMoney(r.cashCents)}</strong> cash
                </span>
                <span>
                  <strong>{formatCount(r.options)}</strong> options
                </span>
              </p>
              {r.impact.map((line) => (
                <p key={line} className={s.meta}>
                  {line}
                </p>
              ))}
              <p className={s.meta}>{r.decision}</p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// --- Sector Leads -----------------------------------------------------------------------------

/**
 * Who leads each sector, read-only (a System Admin keeps the list). Hidden when it can't be read;
 * before anyone leads a sector, it says so (APP-093).
 */
function SectorLeadsCard({ pools }: { pools: readonly PoolPosition[] }): ReactElement | null {
  const { data: me } = useSuspenseQuery(meQuery);
  const list = useQuery(sectorSetupListQuery);
  const sectors = ledSectors(list.data?.sectors ?? [], pools);
  if (list.isError) return null;
  const none = list.isSuccess && sectors.length === 0;
  // Only a System Admin can name a Sector Lead, so only they get the way there.
  const canName = me.roles.some((r) => r.role === 'system_admin');
  return (
    <Card
      title="Sector Leads"
      titleSize="sm"
      compact
      tone="dark"
      help={{
        shows: 'Who leads each sector’s pool.',
        canDo:
          'Nothing to change here. A System Admin updates Sector Leads under Settings › System administration › Roles & lists.',
      }}
      aside={<span className={s.onDarkMeta}>Read-only</span>}
    >
      {list.isPending ? (
        <p className={s.onDarkMeta}>Loading…</p>
      ) : none ? (
        <EmptyState
          variant="not-set"
          art="people"
          size="sm"
          onDark
          headline="No Sector Leads yet"
          body="Each sector’s lead shows here once a System Admin names them in Settings › System administration › Roles & lists."
          {...(canName && {
            action: { label: 'Name the Sector Leads', href: '/settings/system/roles' },
          })}
        />
      ) : (
        <ul className={s.leadList} aria-label="Sector Leads">
          {sectors.map((sector) => (
            <li key={sector.id} className={s.leadRow}>
              <span className={s.leadName}>{sector.lead?.name ?? 'No Sector Lead yet'}</span>
              <span className={s.onDarkMeta}>{sector.name}</span>
            </li>
          ))}
        </ul>
      )}
      <p className={s.onDarkMeta}>Kept by System Admin</p>
    </Card>
  );
}

// --- Recognition coverage ---------------------------------------------------------------------

function CoverageCard({
  fiscalYear,
  firstRun,
}: {
  fiscalYear: number;
  firstRun: boolean;
}): ReactElement | null {
  const overview = useQuery(overviewQuery({ fiscalYear }));
  const recognition = overview.data?.recognition ?? null;
  // Readers who can't open the heat map don't get its summary either.
  if (overview.isError || (overview.isSuccess && recognition === null)) return null;
  const year = String(fiscalYear);
  const rows = recognition?.sectors ?? [];
  const nobody = firstRun || rows.every((r) => r.recognized === 0);
  return (
    <Card
      title="Recognition coverage"
      titleSize="sm"
      compact
      help={{
        shows: `The share of each sector’s people recognized at least once in ${year}. A sector of ${SMALL_GROUP_WORDS.toLowerCase()} shows its counts instead, with no bar.`,
        canDo:
          'Spot a sector that’s falling behind, then open the recognition report for names and detail.',
      }}
    >
      <p className={s.meta}>{`Share of each sector’s people recognized in ${year}`}</p>
      {overview.isPending ? (
        <p className={s.meta}>Loading…</p>
      ) : (
        <div className={s.coverageWrap}>
          <ul className={cx(s.coverageList, nobody && s.coverageGhost)} aria-hidden={nobody}>
            {rows.map((row) => {
              // APP-197: a sector of fewer than SMALL_GROUP people has no percent (the server sends
              // null): its counts, and words where the bar would be, never a 0% stand-in.
              if (row.percent === null)
                return (
                  <li key={row.sector.id} className={s.coverageRow}>
                    <span className={s.coverageName}>{row.sector.name}</span>
                    <span className={s.coveragePct}>
                      {shareWords(nobody ? 0 : row.recognized, row.people, null)}
                    </span>
                    <span className={s.coverageNote}>{SMALL_GROUP_WORDS}</span>
                  </li>
                );
              const pct = nobody ? 0 : row.percent;
              return (
                <li key={row.sector.id} className={s.coverageRow}>
                  <span className={s.coverageName}>{row.sector.name}</span>
                  <span className={s.coveragePct}>{`${String(pct)}%`}</span>
                  <span className={s.coverageTrack}>
                    <span className={s.coverageFill} style={{ width: `${String(pct)}%` }} />
                  </span>
                </li>
              );
            })}
          </ul>
          {nobody && (
            <div className={s.coverageEmpty}>
              <p className={s.strong}>0% recognized so far, in every sector</p>
              <p className={s.meta}>
                Bars fill in as Sector Leads, PAAs and TAAs recognize people this year.
              </p>
            </div>
          )}
        </div>
      )}
      <Link href="/insights/heat-map" className={cx(s.strongLink, s.footLink)}>
        Open the recognition report
      </Link>
    </Card>
  );
}
